/**
 * delete-org.tsx — Xoá tổ chức 2 bước (Platform):
 *  • DeleteOrgCard (tab Cài đặt): xoá mềm — khoá đăng nhập + kênh, ẩn khỏi danh sách, khôi phục được.
 *  • DeletedOrgBanner (đầu trang chi tiết khi đã xoá): Khôi phục · Xoá vĩnh viễn (gõ đúng tên tổ chức).
 * Backend: platform/org-delete-service.ts, org-profile-routes.ts (POST delete / restore / purge).
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, RotateCcw, Trash2, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { platformApiError } from '@/lib/platform-client'
import { useDeleteOrg, usePurgeOrg, useRestoreOrg, type OrgOverview } from '@/hooks/use-platform-org-manage'
import { ReasonDialog } from './reason-dialog'
import { reasonError } from './reason'
import { fmtDateTime, fmtDay } from './labels'

/** So tên xác nhận như server: bỏ khoảng trắng thừa, không phân biệt hoa thường. */
export function confirmNameMatches(input: string, orgName: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()
  return norm(input) !== '' && norm(input) === norm(orgName)
}

export function DeleteOrgCard({ orgId, orgName, purgeAfterDays }: { orgId: string; orgName: string; purgeAfterDays: number }) {
  const m = useDeleteOrg(orgId)
  const [open, setOpen] = useState(false)
  const keep = purgeAfterDays > 0
    ? `Khôi phục được trong ${purgeAfterDays} ngày; sau đó dữ liệu bị xoá vĩnh viễn tự động.`
    : 'Khôi phục được bất cứ lúc nào cho tới khi bấm "Xoá vĩnh viễn".'
  return (
    <Card className="border-destructive/40 lg:col-span-2">
      <CardHeader><CardTitle className="text-destructive">Xoá tổ chức</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">
          Mọi nhân viên bị đăng xuất và không đăng nhập được, mọi kênh bị khoá (Zalo ngắt kết nối), tổ chức chuyển sang mục "Đã xoá". {keep}
        </p>
        <p className="text-xs text-muted-foreground">
          Xoá vĩnh viễn xoá sạch hội thoại, khách hàng, kênh, nhân viên… — đơn thanh toán / hoá đơn được lưu bản sao trong nhật ký Platform.
        </p>
        <Button variant="destructive" onClick={() => setOpen(true)}><Trash2 className="h-4 w-4" /> Xoá tổ chức</Button>
      </CardContent>
      <ReasonDialog
        open={open} onOpenChange={setOpen} title={`Xoá tổ chức "${orgName}"`} destructive
        description={`Nhân viên bị đăng xuất, kênh bị khoá. ${keep}`}
        confirmLabel="Xoá tổ chức" pending={m.isPending}
        onConfirm={async (reason) => {
          try {
            const r = await m.mutateAsync({ reason })
            toast.success(`Đã xoá tổ chức · khoá ${r.disabledChannels} kênh · đăng xuất ${r.revokedSessions} phiên`)
            setOpen(false)
          } catch (err) { toast.error(platformApiError(err)) }
        }}
      />
    </Card>
  )
}

export function DeletedOrgBanner({ org }: { org: OrgOverview['org'] }) {
  const navigate = useNavigate()
  const restore = useRestoreOrg(org.id)
  const [restoreOpen, setRestoreOpen] = useState(false)
  const [purgeOpen, setPurgeOpen] = useState(false)
  if (!org.deletedAt) return null
  return (
    <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
      <div className="flex flex-wrap items-start gap-3">
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1 space-y-1 text-sm">
          <p className="font-medium text-destructive">
            {org.purging ? 'Đang xoá vĩnh viễn tổ chức này…' : `Tổ chức đã bị xoá lúc ${fmtDateTime(org.deletedAt)}`}
          </p>
          {!org.purging && (
            <p className="text-muted-foreground">
              Đăng nhập và kênh đang bị khoá; chỉ còn xem, khôi phục hoặc xoá vĩnh viễn.
              {org.purgeAt ? ` Tự xoá vĩnh viễn ngày ${fmtDay(org.purgeAt)}.` : ' Không tự xoá vĩnh viễn.'}
            </p>
          )}
          {org.deleteReason && <p className="text-muted-foreground">Lý do xoá: {org.deleteReason}</p>}
        </div>
        {!org.purging && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setRestoreOpen(true)}><RotateCcw className="h-4 w-4" /> Khôi phục</Button>
            <Button variant="destructive" onClick={() => setPurgeOpen(true)}><Trash2 className="h-4 w-4" /> Xoá vĩnh viễn</Button>
          </div>
        )}
      </div>
      <ReasonDialog
        open={restoreOpen} onOpenChange={setRestoreOpen} title={`Khôi phục "${org.name}"`}
        description="Trả lại trạng thái trước khi xoá và mở lại các kênh đã bị khoá lúc xoá (Zalo kết nối lại). Nhân viên đăng nhập lại được."
        confirmLabel="Khôi phục" pending={restore.isPending}
        onConfirm={async (reason) => {
          try {
            const r = await restore.mutateAsync({ reason })
            toast.success(`Đã khôi phục tổ chức · mở lại ${r.reopenedChannels} kênh`)
            setRestoreOpen(false)
          } catch (err) { toast.error(platformApiError(err)) }
        }}
      />
      <PurgeDialog
        open={purgeOpen} onOpenChange={setPurgeOpen} orgId={org.id} orgName={org.name}
        onDone={() => navigate('/platform/companies')}
      />
    </div>
  )
}

function PurgeDialog({ open, onOpenChange, orgId, orgName, onDone }: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: string; orgName: string; onDone: () => void
}) {
  const m = usePurgeOrg(orgId)
  const [name, setName] = useState('')
  const [reason, setReason] = useState('')
  const nameOk = confirmNameMatches(name, orgName)
  const reasonErr = reasonError(reason, true)

  function close(v: boolean) {
    if (!v) { setName(''); setReason('') }
    onOpenChange(v)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!nameOk || reasonErr) return
    try {
      await m.mutateAsync({ reason: reason.trim(), confirmName: name })
      toast.success(`Đang xoá vĩnh viễn "${orgName}" — chạy nền, vài phút với tổ chức lớn`)
      close(false)
      onDone()
    } catch (err) { toast.error(platformApiError(err)) }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-destructive">Xoá vĩnh viễn "{orgName}"</DialogTitle>
          <DialogDescription>
            Xoá sạch toàn bộ dữ liệu: nhân viên, hội thoại, tin nhắn, khách hàng, kênh, AI, cài đặt. <b>Không khôi phục được.</b>{' '}
            Đơn thanh toán, hoá đơn và ví token được lưu bản sao trong nhật ký Platform.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="purge-name">Gõ đúng tên tổ chức để xác nhận: <span className="font-semibold">{orgName}</span></Label>
            <Input id="purge-name" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} placeholder={orgName} />
            {name.trim() && !nameOk && <p className="text-xs text-destructive">Tên chưa khớp</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="purge-reason">Lý do <span className="text-destructive">*</span></Label>
            <Textarea id="purge-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ghi rõ lý do — lưu vào nhật ký quản trị Platform" />
            {reasonErr && reason.trim() && <p className="text-xs text-destructive">{reasonErr}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)}>Huỷ</Button>
            <Button type="submit" variant="destructive" disabled={!nameOk || !!reasonErr || m.isPending}>
              {m.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Xoá vĩnh viễn
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
